/** @jsxImportSource @emotion/react */
"use client";

import type { Interpolation } from "@emotion/react";
import { keyframes } from "@emotion/react";
import * as SheetPrimitive from "@radix-ui/react-dialog";
import * as React from "react";

import type { ThemeType } from "../../../../global/emotion";
import {
  constantSemanticColors,
  fontSizes,
  fontWeights,
  radius,
  spacing,
  zIndices,
} from "../../../../global/emotion";
import { useThemeValues } from "../../../hooks/useThemeValues";
import { XIcon } from "../icons/Icon/ExternalIcons";

// Animation keyframes
const fadeIn = keyframes({
  "0%": { opacity: 0 },
  "100%": { opacity: 1 },
});

const fadeOut = keyframes({
  "0%": { opacity: 1 },
  "100%": { opacity: 0 },
});

const slideInFromRight = keyframes({
  "0%": { transform: "translateX(100%)" },
  "100%": { transform: "translateX(0)" },
});

const slideOutToRight = keyframes({
  "0%": { transform: "translateX(0)" },
  "100%": { transform: "translateX(100%)" },
});

const slideInFromLeft = keyframes({
  "0%": { transform: "translateX(-100%)" },
  "100%": { transform: "translateX(0)" },
});

const slideOutToLeft = keyframes({
  "0%": { transform: "translateX(0)" },
  "100%": { transform: "translateX(-100%)" },
});

const slideInFromTop = keyframes({
  "0%": { transform: "translateY(-100%)" },
  "100%": { transform: "translateY(0)" },
});

const slideOutToTop = keyframes({
  "0%": { transform: "translateY(0)" },
  "100%": { transform: "translateY(-100%)" },
});

const slideInFromBottom = keyframes({
  "0%": { transform: "translateY(100%)" },
  "100%": { transform: "translateY(0)" },
});

const slideOutToBottom = keyframes({
  "0%": { transform: "translateY(0)" },
  "100%": { transform: "translateY(100%)" },
});

// Styles
const overlayStyle = {
  position: "fixed" as const,
  inset: 0,
  zIndex: zIndices.modalOverlay,
  backgroundColor: constantSemanticColors.fixedDarkenMedHigh,

  "&[data-state='open']": {
    animation: `${fadeIn} 150ms cubic-bezier(0.16, 1, 0.3, 1)`,
  },
  "&[data-state='closed']": {
    animation: `${fadeOut} 150ms cubic-bezier(0.4, 0, 1, 1)`,
  },
};

const getContentStyle = (
  theme: ThemeType,
  side: "top" | "right" | "bottom" | "left",
): Record<string, any> => {
  const baseStyle: Record<string, any> = {
    backgroundColor: theme.semanticColors.background,
    position: "fixed" as const,
    zIndex: zIndices.modalContent,
    display: "flex",
    flexDirection: "column" as const,
    gap: spacing.m,
    boxShadow:
      "0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)",
    transition: "ease-in-out",
    "&[data-state='closed']": {
      transitionDuration: "300ms",
    },
    "&[data-state='open']": {
      transitionDuration: "500ms",
    },
  };

  switch (side) {
    case "right":
      baseStyle.insetBlock = 0;
      baseStyle.right = 0;
      baseStyle.height = "100%";
      baseStyle.width = "75%";
      baseStyle.borderLeft = `1px solid ${theme.semanticColors.neutralsLower}`;
      baseStyle["@media (min-width: 640px)"] = {
        maxWidth: spacing.sidebar || "384px", // sm:max-w-sm
      };
      baseStyle["&[data-state='closed']"] = {
        ...baseStyle["&[data-state='closed']"],
        animation: `${slideOutToRight} 300ms cubic-bezier(0.4, 0, 1, 1)`,
      };
      baseStyle["&[data-state='open']"] = {
        ...baseStyle["&[data-state='open']"],
        animation: `${slideInFromRight} 500ms cubic-bezier(0.16, 1, 0.3, 1)`,
      };
      break;
    case "left":
      baseStyle.insetBlock = 0;
      baseStyle.left = 0;
      baseStyle.height = "100%";
      baseStyle.width = "75%";
      baseStyle.borderRight = `1px solid ${theme.semanticColors.neutralsLower}`;
      baseStyle["@media (min-width: 640px)"] = {
        maxWidth: spacing.sidebar || "384px",
      };
      baseStyle["&[data-state='closed']"] = {
        ...baseStyle["&[data-state='closed']"],
        animation: `${slideOutToLeft} 300ms cubic-bezier(0.4, 0, 1, 1)`,
      };
      baseStyle["&[data-state='open']"] = {
        ...baseStyle["&[data-state='open']"],
        animation: `${slideInFromLeft} 500ms cubic-bezier(0.16, 1, 0.3, 1)`,
      };
      break;
    case "top":
      baseStyle.insetInline = 0;
      baseStyle.top = 0;
      baseStyle.height = "auto";
      baseStyle.borderBottom = `1px solid ${theme.semanticColors.neutralsLower}`;
      baseStyle["&[data-state='closed']"] = {
        ...baseStyle["&[data-state='closed']"],
        animation: `${slideOutToTop} 300ms cubic-bezier(0.4, 0, 1, 1)`,
      };
      baseStyle["&[data-state='open']"] = {
        ...baseStyle["&[data-state='open']"],
        animation: `${slideInFromTop} 500ms cubic-bezier(0.16, 1, 0.3, 1)`,
      };
      break;
    case "bottom":
      baseStyle.insetInline = 0;
      baseStyle.bottom = 0;
      baseStyle.height = "auto";
      baseStyle.borderTop = `1px solid ${theme.semanticColors.neutralsLower}`;
      baseStyle["&[data-state='closed']"] = {
        ...baseStyle["&[data-state='closed']"],
        animation: `${slideOutToBottom} 300ms cubic-bezier(0.4, 0, 1, 1)`,
      };
      baseStyle["&[data-state='open']"] = {
        ...baseStyle["&[data-state='open']"],
        animation: `${slideInFromBottom} 500ms cubic-bezier(0.16, 1, 0.3, 1)`,
      };
      break;
  }

  return baseStyle;
};

const closeButtonStyle: Interpolation<ThemeType> = (theme) => ({
  position: "absolute" as const,
  top: spacing.m,
  right: spacing.m,
  borderRadius: radius.xs,
  opacity: 0.7,
  transition: "opacity 150ms",
  backgroundColor: theme.semanticColors.secondary,
  border: "none",
  cursor: "pointer",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: spacing.xs,
  color: theme.semanticColors.primary,

  "&:hover": {
    opacity: 1,
  },

  "&:focus": {
    outline: `2px solid ${theme.semanticColors.primary}`,
    outlineOffset: "2px",
  },

  "&:disabled": {
    pointerEvents: "none" as const,
  },

  "&[data-state='open']": {
    backgroundColor: theme.semanticColors.secondary,
  },
});

const srOnlyStyle = {
  position: "absolute" as const,
  width: "1px",
  height: "1px",
  padding: 0,
  margin: "-1px",
  overflow: "hidden",
  clip: "rect(0, 0, 0, 0)",
  whiteSpace: "nowrap" as const,
  borderWidth: 0,
};

function Sheet({ ...props }: React.ComponentProps<typeof SheetPrimitive.Root>) {
  return <SheetPrimitive.Root data-slot="sheet" {...props} />;
}

function SheetTrigger({
  ...props
}: React.ComponentProps<typeof SheetPrimitive.Trigger>) {
  return <SheetPrimitive.Trigger data-slot="sheet-trigger" {...props} />;
}

function SheetClose({
  ...props
}: React.ComponentProps<typeof SheetPrimitive.Close>) {
  return <SheetPrimitive.Close data-slot="sheet-close" {...props} />;
}

function SheetPortal({
  ...props
}: React.ComponentProps<typeof SheetPrimitive.Portal>) {
  return <SheetPrimitive.Portal data-slot="sheet-portal" {...props} />;
}

function SheetOverlay({
  ...props
}: React.ComponentProps<typeof SheetPrimitive.Overlay>) {
  return (
    <SheetPrimitive.Overlay
      data-slot="sheet-overlay"
      css={overlayStyle}
      {...props}
    />
  );
}

function SheetContent({
  children,
  side = "right",
  showCloseButton = true,
  ...props
}: React.ComponentProps<typeof SheetPrimitive.Content> & {
  side?: "top" | "right" | "bottom" | "left";
  showCloseButton?: boolean;
}) {
  const theme = useThemeValues();
  const contentStyle = React.useMemo(
    () => getContentStyle(theme, side),
    [theme, side],
  );

  return (
    <SheetPortal>
      <SheetOverlay />
      <SheetPrimitive.Content
        data-slot="sheet-content"
        css={contentStyle}
        {...props}
      >
        {children}
        {showCloseButton && (
          <SheetPrimitive.Close css={closeButtonStyle}>
            <XIcon />
            <span css={srOnlyStyle}>Close</span>
          </SheetPrimitive.Close>
        )}
      </SheetPrimitive.Content>
    </SheetPortal>
  );
}

function SheetHeader({ ...props }: React.ComponentProps<"div">) {
  const headerStyle = {
    display: "flex",
    flexDirection: "column" as const,
    gap: spacing.ms,
    padding: spacing.m,
  };

  return <div data-slot="sheet-header" css={headerStyle} {...props} />;
}

function SheetFooter({ ...props }: React.ComponentProps<"div">) {
  const footerStyle = {
    marginTop: "auto",
    display: "flex",
    flexDirection: "column" as const,
    gap: spacing.s,
    padding: spacing.m,
  };

  return <div data-slot="sheet-footer" css={footerStyle} {...props} />;
}

function SheetTitle({
  ...props
}: React.ComponentProps<typeof SheetPrimitive.Title>) {
  const titleStyle = (theme: ThemeType) => ({
    color: theme.semanticColors.primary,
    fontWeight: fontWeights.titleM,
    fontSize: fontSizes.titleM,
  });

  return (
    <SheetPrimitive.Title data-slot="sheet-title" css={titleStyle} {...props} />
  );
}

function SheetDescription({
  ...props
}: React.ComponentProps<typeof SheetPrimitive.Description>) {
  const descriptionStyle = (theme: ThemeType) => ({
    color: theme.semanticColors.neutralsMedium,
    fontSize: fontSizes.bodyM,
  });

  return (
    <SheetPrimitive.Description
      data-slot="sheet-description"
      css={descriptionStyle}
      {...props}
    />
  );
}

export {
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetOverlay,
  SheetPortal,
  SheetTitle,
  SheetTrigger,
};
